import { faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type PropsWithChildren, type ReactNode, useEffect } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const modalSizes = ['sm', 'base', 'lg', 'xl'] as const;
export type ModalSize = (typeof modalSizes)[number];

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  base: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export interface ModalProps extends PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  /** Optional title rendered in the standard header bar. When set,
   * `<Modal.Body>` and `<Modal.Footer>` slots align with it. */
  title?: ReactNode;
  /** Hide the default X close button (e.g. for modals with their own
   * "Save / Cancel" footer that should be the only dismiss path). */
  hideCloseButton?: boolean;
  /** Width preset. Defaults to `base` (~28rem). */
  size?: ModalSize;
  /** Disable dismiss-on-scrim-click. Useful for destructive flows where an
   * accidental click outside shouldn't quietly cancel. */
  disableScrimDismiss?: boolean;
  className?: ClassName;
}

/**
 * Centered modal with a scrim. Esc-to-close + scrim-click-to-close +
 * focus-trap-light (the existing modals didn't have a trap, so this
 * primitive doesn't introduce one yet).
 *
 * Body content is the `children` slot. For modals that need a header bar +
 * footer bar separated by a scrolling body, use `<Modal.Header>` /
 * `<Modal.Body>` / `<Modal.Footer>` from this module.
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  hideCloseButton = false,
  size = 'base',
  disableScrimDismiss = false,
  className,
  children,
}: ModalProps) => {
  // Esc closes — keeps muscle-memory dismiss working with no extra wiring.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <>
      <button
        type="button"
        onClick={disableScrimDismiss ? undefined : onClose}
        aria-label="Close"
        tabIndex={disableScrimDismiss ? -1 : 0}
        className={twx(
          'fixed inset-0 bg-content-scrim z-modal-scrim motion-safe:animate-kawaii-modal-scrim',
          disableScrimDismiss ? 'cursor-default' : 'cursor-pointer',
        )}
      />
      <div className="fixed inset-0 z-modal flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          className={twx(
            'relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden pointer-events-auto motion-safe:animate-kawaii-modal-dialog',
            sizeClasses[size],
            className,
          )}
        >
          {title !== undefined && (
            <div className="px-5 py-3 border-b border-content-divider">
              {typeof title === 'string' ? (
                <h2 className="font-diary text-xl text-content-text">{title}</h2>
              ) : (
                title
              )}
            </div>
          )}
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-content-focal flex items-center justify-center text-content-subtle hover:text-content-text transition-colors z-10"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} style={{ width: 14, height: 14 }} />
            </button>
          )}
          {children}
        </div>
      </div>
    </>
  );
};

const Body = ({ className, children }: PropsWithChildren<{ className?: ClassName }>) => (
  <div className={twx('p-6', className)}>{children}</div>
);
const Footer = ({ className, children }: PropsWithChildren<{ className?: ClassName }>) => (
  <div
    className={twx(
      'flex items-center justify-end gap-2 px-5 py-3.5 bg-content-focal/40 border-t border-content-divider',
      className,
    )}
  >
    {children}
  </div>
);

// Compound exports for the canonical 3-region layout.
Modal.Body = Body;
Modal.Footer = Footer;
